import { EQUIPMENT } from '../../../equipment/constants'
import { MODEL_KEY } from '../../../model'
import { defineMessages } from 'react-intl'
import { invertObjectUtil } from 'packages/src'

const EXERCISE_KEY = {
  ...MODEL_KEY,

  NAME: 'name',
  LEVEL: 'level',
  FORCE: 'force',
  TYPE: 'type',
  MUSCLES: 'muscles',
  MECHANICS: 'mechanics',
  METABOLIC_EQUIVALENT: 'metabolicEquivalent',
  EQUIPMENT: 'equipment',
  SECONDARY_MUSCLES: 'secondaryMuscles',

  // Used to calculate the training volume for bodyweight exercises
  BODYWEIGHT_LOAD_FACTOR: 'bodyweightLoadFactor',

  SHARED_AT: 'sharedAt',
  RECOMMENDED_AT: 'recommendedAt',

  VIDEO: 'video',
  IMAGE: 'image',

  INSTRUCTIONS: 'instructions',

  // Foreign keys
  PROFILE_ID: 'profileId',

  // Associations
  RECOMMENDED: 'recommended',
  ALTERNATIVES: 'alternatives',
  DETAILS: 'details',

  // Virtual association
  // See `ExerciseMedia`
  MEDIA: 'media'

}

const EXERCISE_PRIVATE_KEYS = [
  EXERCISE_KEY.INSTRUCTIONS
]

export {
  EXERCISE_PRIVATE_KEYS,
  EXERCISE_KEY
}
const EXERCISE_CLASS_NAME = 'Exercise'

export const EXERCISE_ASSOCIATION_KEYS = [
  EXERCISE_KEY.MEDIA
]

export { EXERCISE_CLASS_NAME }
const EXERCISE_AUDIT = {
  CREATED: 'Exercise Created',
  UPDATED: 'Exercise Updated'
}

export { EXERCISE_AUDIT }
const EXERCISE_FORCE = {
  PUSH: 1,
  PUSH_UNILATERAL: 2,
  PULL: 3,
  PULL_UNILATERAL: 4,
  ISOMETRIC: 5,
  COMPRESSION: 6
}

export { EXERCISE_FORCE }
const EXERCISE_MECHANICS = {
  COMPOUND: 1,
  ISOLATION: 2
}

export { EXERCISE_MECHANICS }

const EXERCISE_SCOPE = {

  /**
   * Only returns the public attributes of the Exercise.
   * @type {Object}
   */
  defaultScope: {
    attributes: {
      exclude: EXERCISE_PRIVATE_KEYS
    }
  }

}

export { EXERCISE_SCOPE }

const EXERCISE_TYPE = {
  STRENGTH: 1,
  POWERLIFTING: 2,
  OLYMPIC_WEIGHTLIFTING: 3,
  STRONGMAN: 4,
  PLYOMETRIC: 5,
  CARDIO: 6,
  CONDITIONING: 7,
  STRETCHING: 8,
  BALANCE: 9,
  SMR: 10,
  WARMUP: 11,
  YOGA: 12,
  ACTIVITY: 13
}

const EXERCISE_TYPE_TO_KEY = invertObjectUtil(EXERCISE_TYPE)

export {
  EXERCISE_TYPE,
  EXERCISE_TYPE_TO_KEY
}
const EXERCISE_TYPE_TO_EQUIPMENT = {
  [EXERCISE_TYPE.WARMUP]: [
    EQUIPMENT.BODYWEIGHT
    // EQUIPMENT.RESISTANCE_BAND
  ],
  [EXERCISE_TYPE.STRENGTH]: [
    EQUIPMENT.BARBELL,
    EQUIPMENT.DUMBBELL,
    EQUIPMENT.KETTLEBELL,
    EQUIPMENT.BODYWEIGHT,
    EQUIPMENT.CABLE,
    EQUIPMENT.PULL_UP_BAR,
    EQUIPMENT.DIP_BAR,
    EQUIPMENT.EZ_BAR,
    EQUIPMENT.MACHINE,
    EQUIPMENT.SMITH_MACHINE
  ],
  [EXERCISE_TYPE.CARDIO]: [
    EQUIPMENT.TREADMILL,
    EQUIPMENT.ELLIPTICAL,
    EQUIPMENT.BICYCLE,
    EQUIPMENT.STAIRMASTER,
    EQUIPMENT.ROWER,
    EQUIPMENT.STATIONARY_BIKE
  ],
  [EXERCISE_TYPE.PLYOMETRIC]: [
    EQUIPMENT.BODYWEIGHT
  ],
  [EXERCISE_TYPE.STRETCHING]: [
    EQUIPMENT.BODYWEIGHT
    // EQUIPMENT.FOAM_ROLLER
  ]
}

export { EXERCISE_TYPE_TO_EQUIPMENT }

const METABOLIC_EQUIVALENT_RANGE = {
  SEDENTARY: [
    1,
    2
  ],
  LIGHT: [
    2,
    3
  ],
  MODERATE: [
    3,
    6
  ],
  VIGOROUS: [
    6,
    10
  ],
  VERY_VIGOROUS: [
    10,
    16
  ]
}

// const METABOLIC_EQUIVALENT = {
//   SEDENTARY: (METABOLIC_EQUIVALENT_RANGE.SEDENTARY[0] +
// METABOLIC_EQUIVALENT_RANGE.SEDENTARY[1]) / 2, LIGHT:
// (METABOLIC_EQUIVALENT_RANGE.LIGHT[0] + METABOLIC_EQUIVALENT_RANGE.LIGHT[1])
// / 2, MODERATE: (METABOLIC_EQUIVALENT_RANGE.MODERATE[0] +
// METABOLIC_EQUIVALENT_RANGE.MODERATE[1]) / 2, VIGOROUS:
// (METABOLIC_EQUIVALENT_RANGE.VIGOROUS[0] +
// METABOLIC_EQUIVALENT_RANGE.VIGOROUS[1]) / 2, VERY_VIGOROUS:
// (METABOLIC_EQUIVALENT_RANGE.VERY_VIGOROUS[0] +
// METABOLIC_EQUIVALENT_RANGE.VERY_VIGOROUS[1]) / 2 }

const EXERCISE_TYPE_TO_METABOLIC_EQUIVALENT = {
  [EXERCISE_TYPE.STRENGTH]: METABOLIC_EQUIVALENT_RANGE.VERY_VIGOROUS[0],
  [EXERCISE_TYPE.POWERLIFTING]: METABOLIC_EQUIVALENT_RANGE.VERY_VIGOROUS[0],
  [EXERCISE_TYPE.OLYMPIC_WEIGHTLIFTING]: METABOLIC_EQUIVALENT_RANGE.VERY_VIGOROUS[0], // [EXERCISE_TYPE.STRONGMAN]: (METABOLIC_EQUIVALENT_RANGE.VIGOROUS[0] + METABOLIC_EQUIVALENT_RANGE.VIGOROUS[1]) / 2,
  [EXERCISE_TYPE.PLYOMETRIC]: (
    METABOLIC_EQUIVALENT_RANGE.VERY_VIGOROUS[0] +
    METABOLIC_EQUIVALENT_RANGE.VERY_VIGOROUS[1]
  ) / 2,
  [EXERCISE_TYPE.CARDIO]: METABOLIC_EQUIVALENT_RANGE.VERY_VIGOROUS[1],
  [EXERCISE_TYPE.CONDITIONING]: (
    METABOLIC_EQUIVALENT_RANGE.VERY_VIGOROUS[0] +
    METABOLIC_EQUIVALENT_RANGE.VERY_VIGOROUS[1]
  ) / 2,
  [EXERCISE_TYPE.STRETCHING]: METABOLIC_EQUIVALENT_RANGE.LIGHT[0],
  [EXERCISE_TYPE.BALANCE]: METABOLIC_EQUIVALENT_RANGE.LIGHT[0],
  [EXERCISE_TYPE.SMR]: METABOLIC_EQUIVALENT_RANGE.LIGHT[0],
  [EXERCISE_TYPE.WARMUP]: METABOLIC_EQUIVALENT_RANGE.LIGHT[0],
  [EXERCISE_TYPE.YOGA]: METABOLIC_EQUIVALENT_RANGE.VIGOROUS[0],
  [EXERCISE_TYPE.ACTIVITY]: (
    METABOLIC_EQUIVALENT_RANGE.VERY_VIGOROUS[0] +
    METABOLIC_EQUIVALENT_RANGE.VERY_VIGOROUS[1]
  ) / 2
}

const METABOLIC_EQUIVALENT_DEFAULT = METABOLIC_EQUIVALENT_RANGE.VERY_VIGOROUS[0]

export {
  METABOLIC_EQUIVALENT_RANGE,
  EXERCISE_TYPE_TO_METABOLIC_EQUIVALENT,
  METABOLIC_EQUIVALENT_DEFAULT
}

const messages = defineMessages({
  strength: {
    id: 'exerciseType.strength',
    defaultMessage: 'Strength',
    description: 'The label used for the strength exercise type.'
  },
  powerlifting: {
    id: 'exerciseType.powerlifting',
    defaultMessage: 'Powerlifting',
    description: 'The label used for the powerlifting exercise type.'
  },
  olympicWeightlifting: {
    id: 'exerciseType.olympicWeightlifting',
    defaultMessage: 'Weightlifting',
    description: 'The label used for the olympic weightlifting exercise type.'
  },
  strongman: {
    id: 'exerciseType.strongman',
    defaultMessage: 'Strongman',
    description: 'The label used for the strongman exercise type.'
  },
  plyometric: {
    id: 'exerciseType.plyometric',
    defaultMessage: 'Plyometric',
    description: 'The label used for the plyometric exercise type.'
  },
  cardio: {
    id: 'exerciseType.cardio',
    defaultMessage: 'Cardio',
    description: 'The label used for the cardio exercise type.'
  },
  conditioning: {
    id: 'exerciseType.conditioning',
    defaultMessage: 'Conditioning',
    description: 'The label used for the conditioning exercise type.'
  },
  stretching: {
    id: 'exerciseType.stretching',
    defaultMessage: 'Stretching',
    description: 'The label used for the stretching exercise type.'
  },
  balance: {
    id: 'exerciseType.balance',
    defaultMessage: 'Balance',
    description: 'The label used for the balance exercise type.'
  },
  smr: {
    id: 'exerciseType.smr',
    defaultMessage: 'SMR',
    description: 'The label used for the smr exercise type.'
  },
  warmup: {
    id: 'exerciseType.warmup',
    defaultMessage: 'Warmup',
    description: 'The label used for the warmup exercise type.'
  },
  yoga: {
    id: 'exerciseType.yoga',
    defaultMessage: 'Yoga',
    description: 'The label used for the yoga exercise type.'
  },
  activity: {
    id: 'exerciseType.activity',
    defaultMessage: 'Activity',
    description: 'The label used for the activity exercise type.'
  }
})

export const EXERCISE_TYPE_MESSAGE = {
  [EXERCISE_TYPE.STRENGTH]: messages.strength,
  [EXERCISE_TYPE.POWERLIFTING]: messages.powerlifting,
  [EXERCISE_TYPE.OLYMPIC_WEIGHTLIFTING]: messages.olympicWeightlifting,
  // EXERCISE_TYPE.STRONGMAN]: messages.strongman,
  [EXERCISE_TYPE.PLYOMETRIC]: messages.plyometric,
  [EXERCISE_TYPE.CARDIO]: messages.cardio,
  [EXERCISE_TYPE.CONDITIONING]: messages.conditioning,
  [EXERCISE_TYPE.STRETCHING]: messages.stretching,
  [EXERCISE_TYPE.BALANCE]: messages.balance,
  [EXERCISE_TYPE.SMR]: messages.smr,
  [EXERCISE_TYPE.WARMUP]: messages.warmup,
  [EXERCISE_TYPE.YOGA]: messages.yoga,
  [EXERCISE_TYPE.ACTIVITY]: messages.activity
}
